import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import { PagingControls } from '../components/PagingControls';
import { SearchFilterBar, useSearchFilter } from '../components/SearchFilterBar';
import { QueueColumnPicker, useQueueColumns } from '../components/QueueColumns';
import { getCurrentUser } from '../auth/useUser';
import { useAppData } from '../state/contexts/AppDataContext';
import { usePollTick } from '../state/usePollTick';
import { useUIState } from '../state/contexts/UIStateContext';
import { getCase } from '../api/cases';
import { isEvidenceImage } from '../api/evidenceFiles';
import { formatOrgType } from '../domain/constants';
import {
  listIntakeQueue,
  getIntakeSubmission,
  promoteIntakeSubmission,
  rejectIntakeSubmission,
  listTnChangeRequests,
  approveTnChangeRequest,
  rejectTnChangeRequest,
  listIntakeFiles,
  intakeImageDataUrl,
  downloadIntakeFile,
  setTnChangeLoaVerdict,
  type IntakeFile,
  type IntakeFileTarget,
  type LoaStatus,
  listCompanyChangeRequests,
  applyCompanyChangeRequest,
  getAffectedRegistrations,
  type AffectedRegistrations,
  rejectCompanyChangeRequest,
  COMPANY_FIELD_LABEL,
  COMPANY_FIELD_ORDER,
  formatCompanyFieldValue,
  type IntakeStatus,
  type IntakeSubmission,
  type TnChangeRequest,
  type CompanyChangeRequest,
} from '../api/intake';

// R11 — analyst Intake queue. External customers submit onboarding data via the
// client-credentials API; it lands here as PENDING. Staff review, then promote
// (→ DRAFT case, re-enters vetting) or reject with feedback the customer sees.
//
// Mirrors the R3 data-layer pattern: a scoped, paged server query + reused
// PagingControls. Submissions aren't `Case`s, so this uses a dedicated
// useIntakeQueue hook rather than useCaseQuery, but the offset/total/refetch
// shape is the same.
interface IntakeQueryResult {
  items: IntakeSubmission[];
  total: number;
  loading: boolean;
  error: string | null;
  offset: number;
  limit: number;
  setOffset: (offset: number) => void;
  refetch: () => void;
}

function useIntakeQueue(status: IntakeStatus | '', limit = 50, paused = false): IntakeQueryResult {
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<IntakeSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  // #474 — refetch on the #148 heartbeat so a submission filed from the
  // customer portal appears without a reload. `paused` holds the tick off
  // while the operator has a promote/reject in flight.
  const pollTick = usePollTick(paused);
  const seqRef = useRef(0);

  // A status-filter change is a new query → reset to the first page.
  useEffect(() => { setOffset(0); }, [status]);

  // One fetch path, two callers. A `background` run shows no spinner and
  // swallows its error: replacing a populated queue with an error banner
  // because one 60-second refresh failed is worse than the staleness it was
  // there to fix. The sequence guard stops a slow background response
  // overwriting a fresher foreground one.
  const runQuery = useCallback(async (background: boolean) => {
    const seq = ++seqRef.current;
    if (!background) setLoading(true);
    try {
      const { items: rows, total: count } = await listIntakeQueue({
        status: status || undefined, limit, offset,
      });
      if (seq !== seqRef.current) return;
      setItems(rows);
      setTotal(count);
      setError(null);
    } catch (err) {
      if (seq !== seqRef.current || background) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      // NOT `&& !background` — see the note in state/useCaseQuery.ts: that
      // guard let `loading` stick at true when a background tick superseded a
      // foreground run.
      if (seq === seqRef.current) setLoading(false);
    }
  }, [status, limit, offset]);

  useEffect(() => { void runQuery(false); }, [runQuery, nonce]);

  // pollTick alone — see the matching note in useCaseQuery.
  useEffect(() => {
    if (pollTick === 0) return;
    void runQuery(true);
  }, [pollTick]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);
  return { items, total, loading, error, offset, limit, setOffset, refetch };
}

function StatusPill({ status }: { status: IntakeStatus }) {
  const map: Record<IntakeStatus, { bg: string; fg: string; label: string }> = {
    PENDING: { bg: C.warnBg, fg: C.warn, label: 'Pending review' },
    PROMOTED: { bg: C.successBg, fg: C.success, label: 'Promoted' },
    REJECTED: { bg: C.dangerBg, fg: C.danger, label: 'Rejected' },
    WITHDRAWN: { bg: C.dangerBg, fg: C.textMuted, label: 'Withdrawn' },
  };
  const s = map[status];
  return <span style={{ ...styles.badge, background: s.bg, color: s.fg }}>{s.label}</span>;
}

export function Intake() {
  // #472 — the sidebar badge counts all three queues on this page, so every
  // decision made here has to refresh it. Without this, working the last
  // pending item leaves a red count in the sidebar until the #148 poll ticks.
  const { showToast, refreshStats } = useAppData();
  const { openCase, focusedSubmissionId, setFocusedSubmissionId } = useUIState();
  const user = getCurrentUser()!;
  const canReview = user.canEdit || user.canApprove || user.canAdmin;

  const [statusFilter, setStatusFilter] = useState<IntakeStatus | ''>('PENDING');
  const [busy, setBusy] = useState(false);
  const query = useIntakeQueue(statusFilter, 50, busy);
  const { filtered, bar } = useSearchFilter(
    query.items,
    (s) => [s.id, s.customerId, s.payload?.legalName, formatOrgType(s.payload?.orgType)].filter(Boolean).join(' '),
    [],
    { placeholder: 'Search by submission, customer, legal name, or org type…' },
  );
  // #474 — selection is held by ID, not by the row object, so a background
  // refetch re-resolves the open detail panel against the fresh rows instead
  // of leaving it pinned to a stale copy (or blanking it, which re-keying by
  // array position would do the moment rows shift).
  //
  // `snapshot` is the last row we actually saw for that id. It is what keeps
  // the panel on screen when the row leaves the list — because another analyst
  // promoted it, or because the operator changed the status filter. Which of
  // those happened is the difference between an alarming message and a
  // meaningless one, so `selectedCtx` records the query the selection was made
  // under and the "someone else actioned this" notice is shown only when that
  // query is unchanged.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<IntakeSubmission | null>(null);
  const [selectedCtx, setSelectedCtx] = useState('');
  const [rejectFeedback, setRejectFeedback] = useState('');
  // #539 — the cross-page link's fetch request: the linked submission id plus a
  // sequence number that makes each consumption a distinct object, so relinking
  // the same id refetches. Set only by the consumption effect below.
  const [linkedReq, setLinkedReq] = useState<{ id: string; seq: number } | null>(null);

  const queryCtx = `${statusFilter}|${query.offset}`;
  const liveSelected = selectedId ? query.items.find((s) => s.id === selectedId) : undefined;
  const selected = liveSelected ?? (selectedId ? snapshot : null);
  // Only claim the row was actioned elsewhere when it vanished from the very
  // list it was picked from, and only once that list has actually loaded.
  const selectionVanished =
    selectedId !== null && !liveSelected && !query.loading && selectedCtx === queryCtx;

  const openSubmission = useCallback((sub: IntakeSubmission) => {
    setSelectedId(sub.id);
    setSnapshot(sub);
    setSelectedCtx(`${statusFilter}|${query.offset}`);
    setRejectFeedback('');
  }, [statusFilter, query.offset]);

  const closeSubmission = useCallback(() => {
    setSelectedId(null);
    setSnapshot(null);
  }, []);

  // Keep the snapshot current while the row is still in the list, so closing
  // the gap between a background refresh and what the panel shows.
  useEffect(() => {
    if (liveSelected) setSnapshot(liveSelected);
  }, [liveSelected]);

  // #539 — arriving here from a link somewhere else in the portal (today: the
  // Registrations tab of a customer, which shows a submission id and had no way
  // to open it).
  //
  // Three things this has to get right, none of them cosmetic:
  //
  //  - WIDEN THE FILTER. The default is PENDING, and the submissions most worth
  //    linking to are the promoted and rejected ones — which are precisely the
  //    rows a PENDING queue does not contain. Left alone, the link would open a
  //    panel floating above a list that cannot hold its row, and closing the
  //    panel would leave the operator staring at a queue the row is absent from.
  //  - FETCH THE ROW, do not hunt for it in the current page. The queue is paged
  //    50 at a time, so a linked row can simply not be in `query.items` whatever
  //    the filter says. The direct read is also fresher than the linking page's
  //    copy, which came from its own bulk query.
  //  - CONSUME THE HANDLE ONCE. Cleared on the way in, so navigating to Intake
  //    again later — from the sidebar, from anywhere — opens the queue and not
  //    whatever row was last linked. Without this the handle is sticky state
  //    that silently re-pins a stale row on every future visit.
  //
  // WHY TWO EFFECTS AND NOT ONE. The obvious single effect clears the handle and
  // fires the fetch in the same body, and it does not work: clearing the handle
  // changes the effect's own dependency, React runs the cleanup, and the cleanup
  // is what cancels the fetch it just started. The panel then never opens and
  // nothing errors. So consumption is synchronous and separate, and the fetch
  // hangs off `linkedReq`, which that effect only ever SETS. A fresh object per
  // consumption on purpose: linking twice to the same id has to refetch, because
  // closing the panel in between dropped the snapshot.
  //
  // `selectedCtx` is deliberately left as '' rather than set to the current
  // query. It is what suppresses the "another reviewer has actioned this"
  // notice, and that notice would be a lie here: this selection was never made
  // from a list, so the row leaving one proves nothing. queryCtx always contains
  // a '|', so '' can never accidentally match it.
  useEffect(() => {
    if (!focusedSubmissionId) return;
    setLinkedReq((prev) => ({ id: focusedSubmissionId, seq: (prev?.seq ?? 0) + 1 }));
    setStatusFilter('');
    setSelectedId(focusedSubmissionId);
    setSelectedCtx('');
    setRejectFeedback('');
    setFocusedSubmissionId(null);
  }, [focusedSubmissionId, setFocusedSubmissionId]);

  useEffect(() => {
    if (!linkedReq) return;
    const { id } = linkedReq;
    // Per-run flag, which is also what makes this correct under StrictMode's
    // double-invoke: the discarded first run's response is dropped, the second
    // run's lands.
    let live = true;
    getIntakeSubmission(id)
      .then((row) => { if (live) setSnapshot(row); })
      .catch((err) => {
        if (!live) return;
        // The panel stays shut rather than opening blank. The widened queue is
        // still useful — the row is very likely in it.
        setSelectedId(null);
        showToast(`Could not open submission ${id}: ${err instanceof Error ? err.message : String(err)}`);
      });
    return () => { live = false; };
  }, [linkedReq, showToast]);

  // #540 — this analyst's own column choice, or the six columns this queue has
  // always shown. Intake reads the SUBMITTED PAYLOAD for the two RCD columns,
  // unlike Cases and Onboarding: there is no case yet, so the payload is the
  // only value that exists, and seeing it here is what lets a reviewer spot a
  // problem BEFORE promoting rather than after.
  const columns = useQueueColumns('intake');

  const cell = (key: string, s: IntakeSubmission): ReactNode => {
    switch (key) {
      case 'submission':
        return <span style={{ color: C.goldText, fontWeight: 600 }}>{s.id}</span>;
      case 'customer':
        return <span style={{ fontSize: 12, color: C.textMuted, fontFamily: 'monospace' }}>{s.customerId}</span>;
      case 'legalName':
        return s.payload?.legalName || '—';
      case 'displayName':
        return s.payload?.displayName || <span style={{ color: C.textMuted }}>—</span>;
      case 'callReason':
        return s.payload?.callReason || <span style={{ color: C.textMuted }}>—</span>;
      case 'orgType':
        return formatOrgType(s.payload?.orgType) || '—';
      case 'submitted':
        return fmtDateTime(s.submittedAt);
      case 'status':
        return <StatusPill status={s.status} />;
      default:
        return null;
    }
  };

  const promote = async (sub: IntakeSubmission) => {
    setBusy(true);
    try {
      const { caseId } = await promoteIntakeSubmission(sub.id);
      showToast(`Submission ${sub.id} promoted to case ${caseId}`, 'success');
      closeSubmission();
      query.refetch();
      refreshStats();
      // Navigate to the freshly created DRAFT case so the analyst can begin
      // vetting immediately.
      try {
        const created = await getCase(caseId);
        openCase(created, 'intake');
      } catch {
        // Promotion succeeded; if the case fetch fails just stay on the queue.
      }
    } catch (err) {
      showToast(`Failed to promote: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const reject = async (sub: IntakeSubmission) => {
    if (!rejectFeedback.trim()) {
      showToast('Rejection feedback is required so the customer knows what to fix.');
      return;
    }
    setBusy(true);
    try {
      await rejectIntakeSubmission(sub.id, rejectFeedback.trim());
      showToast(`Submission ${sub.id} rejected`, 'success');
      closeSubmission();
      setRejectFeedback('');
      query.refetch();
      refreshStats();
    } catch (err) {
      showToast(`Failed to reject: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text }}>Intake Queue</h2>
        <select
          aria-label="Filter by status"
          style={{ ...styles.select, maxWidth: 220 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as IntakeStatus | '')}
        >
          <option value="PENDING">Pending review</option>
          <option value="PROMOTED">Promoted</option>
          <option value="REJECTED">Rejected</option>
          <option value="WITHDRAWN">Withdrawn</option>
          <option value="">All</option>
        </select>
      </div>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>
        Customer-submitted onboarding data. Promote to create a DRAFT vetting case (re-enters the
        standard pipeline) or reject with feedback the customer can read.
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1 }}><SearchFilterBar {...bar} /></div>
        <QueueColumnPicker queue="intake" onError={showToast} />
      </div>

      <div style={styles.card}>
        <table style={styles.table}>
          <thead><tr>
            {columns.map(col => <th key={col.key} style={styles.th}>{col.label}</th>)}
            {/* The Review action is not a selectable column: it has no header
                label to offer in the picker, and hiding the only per-row
                control would leave a queue you cannot act on. */}
            <th style={styles.th}></th>
          </tr></thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => openSubmission(s)}>
                {columns.map(col => <td key={col.key} style={styles.td}>{cell(col.key, s)}</td>)}
                <td style={styles.td}>
                  <button type="button" style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={(e) => { e.stopPropagation(); openSubmission(s); }}>Review</button>
                </td>
              </tr>
            ))}
            {!query.loading && filtered.length === 0 && (
              <tr><td colSpan={columns.length + 1} style={{ ...styles.td, color: C.textMuted, textAlign: 'center' }}>
                {query.items.length === 0 ? `No submissions${statusFilter ? ` with status ${statusFilter}` : ''}` : 'No submissions match the current filters'}
              </td></tr>
            )}
          </tbody>
        </table>
        {query.error && <div style={{ fontSize: 12, color: C.danger, marginTop: 12 }}>Failed to load intake queue: {query.error}</div>}
        <PagingControls offset={query.offset} limit={query.limit} total={query.total} count={query.items.length} onOffset={query.setOffset} label="submissions" />
      </div>

      {selected && (
        <div style={{ ...styles.card, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{selected.payload?.legalName || selected.id}</h3>
              <div style={{ fontSize: 12, color: C.textMuted }}>{selected.id} · customer {selected.customerId} · <StatusPill status={selected.status} /></div>
            </div>
            <button type="button" aria-label="Close detail" style={{ ...styles.btnUnstyled, color: C.textMuted, fontSize: 18, lineHeight: 1 }} onClick={closeSubmission}>×</button>
          </div>

          {selectionVanished && (
            <div role="status" style={{ background: C.warnBg, color: C.warn, padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
              This submission is no longer in the queue — another reviewer has most likely
              actioned it. What you see below is the last copy that was loaded.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 13, marginBottom: 16 }}>
            <Field label="FEIN" value={selected.payload?.fein} />
            <Field label="Org Type" value={formatOrgType(selected.payload?.orgType)} />
            <Field label="Jurisdiction" value={selected.payload?.jurisdiction} />
            <Field label="Website" value={selected.payload?.website} />
            <Field label="Address" value={[selected.payload?.address, selected.payload?.city, selected.payload?.state, selected.payload?.zip].filter(Boolean).join(', ')} />
            <Field label="Display Name" value={selected.payload?.displayName} />
            <Field label="Call Reason" value={selected.payload?.callReason} />
            <Field label="Call Logo URL" value={selected.payload?.callLogoUrl} />
            <Field label="Billing Contact" value={selected.payload?.billingContact ? `${selected.payload.billingContact.name} <${selected.payload.billingContact.email}>` : undefined} />
            {/* #685 — the billing address, which this detail never showed at
                all: the reviewer saw a billing contact's name and email and had
                to open the promoted case to find out where the invoices went.
                Rendered from whichever shape the STORED application carries —
                four parts for one filed since the address was split, the single
                line verbatim for one filed before it. No parse: splitting a
                stored line on commas would invent a city out of whatever came
                second, and this queue is where an application is judged. */}
            <Field label="Billing Address" value={billingAddressLine(selected.payload?.billingContact)} />
            <Field label="Authorizer Contact" value={selected.payload?.authorizerContact ? `${selected.payload.authorizerContact.name} <${selected.payload.authorizerContact.email}>` : undefined} />
            <Field label="OSPIDs" value={selected.payload?.ospids?.join(', ')} />
            <Field label="DIR ID" value={selected.payload?.dirid} />
            <Field label="EID" value={selected.payload?.eid} />
            <Field label="Originating Carrier" value={selected.payload?.originatingCarrier} />
          </div>

          {/* #775 — the customer declared numbers 382 does not provide and sent
              the signed LOA with the application. Read here; VERIFIED on the
              promoted case's TNs tab, which is what the vetting gate reads. */}
          {selected.payload?.notProvidedBy382 === true && (
            <div style={{ background: C.warnBg, color: C.text, padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>One or more numbers is not provided by 382</div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
                The signed Letter of Authorization is below. Once promoted, the case cannot enter vetting until
                &ldquo;LOA verified&rdquo; is ticked on its TNs tab. A letter that cannot be accepted: return the
                application with feedback, and the correction carries a new one.
              </div>
              <LoaFiles key={selected.id} target={{ submissionId: selected.id }} currentFileId={null} />
            </div>
          )}

          {selected.status === 'REJECTED' && selected.analystFeedback && (
            <div style={{ background: C.dangerBg, color: C.danger, padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
              <strong>Rejection feedback sent to customer:</strong> {selected.analystFeedback}
            </div>
          )}
          {selected.status === 'PROMOTED' && selected.promotedCaseId && (
            <div style={{ background: C.successBg, color: C.success, padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
              Promoted to case <strong>{selected.promotedCaseId}</strong>.
            </div>
          )}
          {/* #473 — this submission is a CORRECTION of one we returned. Shown
              with the note we sent, because the reviewer's own earlier feedback
              is the thing to re-read before deciding again: without it, a
              correction arrives looking like a brand-new application and
              whether anybody remembers the return is luck. The returned row is
              untouched — the customer filed a new application, they did not
              reopen the old one. */}
          {selected.supersedesId && (
            <div style={{ background: C.warnBg, color: C.text, padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
              <div>
                Corrects <strong>{selected.supersedesId}</strong>
                {selected.supersedes?.reviewedAt && <> — returned {fmtDateTime(selected.supersedes.reviewedAt)}</>}.
              </div>
              {selected.supersedes?.analystFeedback && (
                <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                  <strong>What we told them:</strong> {selected.supersedes.analystFeedback}
                </div>
              )}
            </div>
          )}

          {canReview && selected.status === 'PENDING' && !selectionVanished && (
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
              <textarea
                aria-label="Rejection feedback"
                placeholder="Feedback for the customer (required to reject)…"
                style={{ ...styles.input, width: '100%', minHeight: 64, marginBottom: 12, resize: 'vertical' }}
                value={rejectFeedback}
                onChange={(e) => setRejectFeedback(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" disabled={busy} style={{ ...styles.btn, ...styles.btnSuccess, ...(busy ? { opacity: 0.6 } : {}) }} onClick={() => promote(selected)}>Promote → Vetting Case</button>
                <button type="button" disabled={busy} style={{ ...styles.btn, ...styles.btnDanger, ...(busy ? { opacity: 0.6 } : {}) }} onClick={() => reject(selected)}>Reject with Feedback</button>
              </div>
            </div>
          )}
          {!canReview && selected.status === 'PENDING' && (
            <div style={{ fontSize: 12, color: C.textMuted }}>Your role can view but not promote or reject submissions.</div>
          )}
        </div>
      )}

      <TnChangeRequestsQueue canReview={canReview} />
      <CompanyChangeRequestsQueue canReview={canReview} />
    </div>
  );
}

// #775 — what the LOA on a TN change request is waiting for, as the queue says it.
export const LOA_STATUS_LABEL: Record<LoaStatus, string> = {
  SUBMITTED: 'LOA to review',
  NEEDED: 'LOA needed from customer',
  VERIFIED: 'LOA verified',
};

/**
 * #775 — the files a customer attached to a request or application, oldest
 * first: its LOA history. The CURRENT one (the TN change request's `loaFileId`,
 * or an application's last, which the API keeps its highest id, #840) is shown: an image inline,
 * through a `data:` URL read with the token; a PDF as a download, never
 * rendered here. Earlier letters are listed and downloadable.
 */
export function LoaFiles({ target, currentFileId }: { target: IntakeFileTarget; currentFileId: number | null }) {
  const [files, setFiles] = useState<IntakeFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const targetKey = JSON.stringify(target);
  useEffect(() => {
    let live = true;
    setFiles(null);
    setPreview(null);
    listIntakeFiles(target)
      .then((rows) => { if (live) setFiles(rows); })
      .catch((err: unknown) => { if (live) setError(err instanceof Error ? err.message : String(err)); });
    return () => { live = false; };
  }, [targetKey, currentFileId]);
  const current = files && files.length > 0
    ? files.find((f) => f.id === currentFileId) ?? files[files.length - 1]
    : null;
  useEffect(() => {
    let live = true;
    if (current && isEvidenceImage(current.contentType)) {
      intakeImageDataUrl(current.id)
        .then((url) => { if (live) setPreview(url); })
        .catch((err: unknown) => { if (live) setError(err instanceof Error ? err.message : String(err)); });
    }
    return () => { live = false; };
  }, [current?.id]);
  const download = (f: IntakeFile) => {
    downloadIntakeFile(f).catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  };
  if (error) return <div role="alert" style={{ fontSize: 12, color: C.danger }}>Could not load the LOA: {error}</div>;
  if (files === null) return <div style={{ fontSize: 12, color: C.textMuted }}>Loading the LOA…</div>;
  if (files.length === 0) return <div style={{ fontSize: 12, color: C.danger }}>No LOA file is attached.</div>;
  return (
    <div style={{ fontSize: 12 }}>
      {preview && current && (
        <img
          src={preview}
          alt={`LOA ${current.filename}`}
          style={{ maxWidth: '100%', maxHeight: 360, border: `1px solid ${C.border}`, borderRadius: 4, display: 'block', marginBottom: 6 }}
        />
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {files.map((f) => (
          <li key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontWeight: f === current ? 600 : 400, color: f === current ? C.text : C.textMuted }}>
              {f.filename}
            </span>
            <span style={{ color: C.textDim }}>{fmtDateTime(f.uploadedAt)}</span>
            {f === current
              ? <span style={{ ...styles.tag, background: C.successBg, color: C.success }}>current</span>
              : <span style={{ ...styles.tag }}>replaced</span>}
            <button
              type="button"
              aria-label={`Download ${f.filename}`}
              style={{ ...styles.btnUnstyled, color: C.goldText, textDecoration: 'underline', fontSize: 12 }}
              onClick={() => download(f)}
            >Download</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * #775 — the LOA on one TN change request: the letter(s), and — for a reviewer,
 * while the request is PENDING — Verify, or LOA needed with the reason the
 * customer is shown. Each verdict names the file it was given on, so a letter
 * the customer replaced in the meantime is refused (409) rather than verified
 * unseen.
 */
function TnChangeLoa({ req, canReview, onChanged }: {
  req: TnChangeRequest;
  canReview: boolean;
  onChanged: () => void;
}) {
  const { showToast } = useAppData();
  const [reason, setReason] = useState('');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async (decision: 'VERIFIED' | 'NEEDED') => {
    if (req.loaFileId === null) return;
    if (decision === 'NEEDED' && !reason.trim()) {
      showToast('A reason is required: it is what the customer is shown.');
      return;
    }
    setBusy(true);
    try {
      await setTnChangeLoaVerdict(req.id, {
        decision, fileId: req.loaFileId, ...(decision === 'NEEDED' ? { reason: reason.trim() } : {}),
      });
      showToast(decision === 'VERIFIED'
        ? `LOA on ${req.id} verified — the request can now be approved`
        : `A new LOA was requested on ${req.id}; the customer is told why`, 'success');
      setAsking(false);
      setReason('');
      onChanged();
    } catch (err) {
      showToast(`Failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ background: C.warnBg, padding: 12, borderRadius: 6 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
        Not provided by 382 — {req.loaStatus ? LOA_STATUS_LABEL[req.loaStatus] : 'no LOA'}
      </div>
      {req.loaStatus === 'NEEDED' && req.loaReason && (
        <div style={{ fontSize: 12, marginBottom: 8, whiteSpace: 'pre-wrap' }}>
          <strong>Asked for a new one:</strong> {req.loaReason}
        </div>
      )}
      <LoaFiles target={{ tnChangeRequestId: req.id }} currentFileId={req.loaFileId} />
      {canReview && req.status === 'PENDING' && req.loaStatus !== 'VERIFIED' && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {asking && (
            <textarea
              aria-label={`Why a new LOA is needed for ${req.id}`}
              placeholder="Why this letter cannot be accepted — the customer is shown this…"
              style={{ ...styles.input, minHeight: 44, resize: 'vertical' }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            {req.loaStatus === 'SUBMITTED' && !asking && (
              <button type="button" disabled={busy} style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }} onClick={() => void send('VERIFIED')}>Verify LOA</button>
            )}
            {req.loaStatus === 'SUBMITTED' && (asking ? (
              <>
                <button type="button" disabled={busy} style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => void send('NEEDED')}>Send: LOA needed</button>
                <button type="button" style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => { setAsking(false); setReason(''); }}>Cancel</button>
              </>
            ) : (
              <button type="button" disabled={busy} style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => setAsking(true)}>LOA needed…</button>
            ))}
            {req.loaStatus === 'NEEDED' && (
              <span style={{ fontSize: 11, color: C.textMuted }}>Waiting for the customer to upload a new letter.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Customer API v2 (#147): staff attestation queue for customer-declared TN
// change requests. Approving is the one write that touches the vetted TN list
// (applies op:A/op:D and sets the case PENDING_SYNC so the #110 propagation
// delivers it); nothing a customer declares reaches cases.tns without this.
function TnChangeRequestsQueue({ canReview }: { canReview: boolean }) {
  const { showToast, refreshStats } = useAppData();
  const [items, setItems] = useState<TnChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [feedbackFor, setFeedbackFor] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  // #474 — same heartbeat as the submissions queue above; paused while this
  // queue has an approve/reject in flight.
  const pollTick = usePollTick(busyId !== null);
  const seqRef = useRef(0);

  const runQuery = useCallback(async (background: boolean) => {
    const seq = ++seqRef.current;
    if (!background) setLoading(true);
    try {
      const { items: rows } = await listTnChangeRequests({ status: 'PENDING' });
      if (seq !== seqRef.current) return;
      setItems(rows);
      setError(null);
    } catch (err) {
      if (seq !== seqRef.current || background) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      // NOT `&& !background` — see the note in state/useCaseQuery.ts: that
      // guard let `loading` stick at true when a background tick superseded a
      // foreground run.
      if (seq === seqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { void runQuery(false); }, [runQuery, nonce]);
  // [pollTick] only, the same form as the other three background effects. This
  // queue's `runQuery` happens to be stable (useCallback with no deps), so
  // listing it would be harmless here and NOT harmless in the two paged
  // queries, where it changes with offset. One form everywhere means the rule
  // survives someone later giving this callback a dependency —
  // exhaustive-deps is off in this config, so this comment is the only
  // backstop.
  useEffect(() => {
    if (pollTick === 0) return;
    void runQuery(true);
  }, [pollTick]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  const approve = async (req: TnChangeRequest) => {
    setBusyId(req.id);
    try {
      const { caseId } = await approveTnChangeRequest(req.id);
      showToast(`TN change ${req.id} attested and staged for case ${caseId}`, 'success');
      refetch();
      refreshStats();
    } catch (err) {
      showToast(`Failed to approve: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyId(null);
    }
  };
  const reject = async (req: TnChangeRequest) => {
    if (!feedback.trim()) {
      showToast('Rejection feedback is required so the customer knows why.');
      return;
    }
    setBusyId(req.id);
    try {
      await rejectTnChangeRequest(req.id, feedback.trim());
      showToast(`TN change ${req.id} rejected`, 'success');
      setFeedbackFor(null);
      setFeedback('');
      refetch();
      refreshStats();
    } catch (err) {
      showToast(`Failed to reject: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ ...styles.card, marginTop: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>Customer TN Change Requests</h3>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 16 }}>
        Numbers a customer has <em>declared</em> via the intake API. Approving attests them onto the
        case's vetted TN list and stages delivery to the Settlement Platform (op:A adds / op:D removals).
        Nothing here reaches CTIA until you approve.
      </div>
      <table style={styles.table}>
        <thead><tr>
          <th style={styles.th}>Request</th>
          <th style={styles.th}>Customer</th>
          <th style={styles.th}>Op</th>
          <th style={styles.th}>Numbers</th>
          <th style={styles.th}>Target</th>
          <th style={styles.th}>Submitted</th>
          <th style={styles.th}></th>
        </tr></thead>
        <tbody>
          {items.map((r) => {
            // #775 — a flagged request is approved only once its LOA is
            // verified; the API refuses it (422) otherwise, and the button
            // says so rather than inviting the refusal.
            const loaBlocks = r.notProvidedBy382 && r.loaStatus !== 'VERIFIED';
            return (
            <Fragment key={r.id}>
            <tr>
              <td style={styles.td}><span style={{ color: C.goldText, fontWeight: 600 }}>{r.id}</span></td>
              <td style={styles.td}><span style={{ fontSize: 12, color: C.textMuted, fontFamily: 'monospace' }}>{r.customerId}</span></td>
              <td style={styles.td}>
                <span style={{ ...styles.tag, background: r.op === 'A' ? C.successBg : C.dangerBg, color: r.op === 'A' ? C.success : C.danger }}>
                  {r.op === 'A' ? 'Add' : 'Remove'}
                </span>
              </td>
              <td style={styles.td}><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.tns.join(', ')}</span></td>
              <td style={styles.td}><span style={{ fontSize: 12, color: C.textMuted }}>{r.caseId || r.submissionId || '—'}</span></td>
              <td style={styles.td}>{fmtDateTime(r.submittedAt)}</td>
              <td style={styles.td}>
                {canReview ? (
                  feedbackFor === r.id ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <textarea
                        aria-label={`Rejection feedback for ${r.id}`}
                        placeholder="Reason (required)…"
                        style={{ ...styles.input, minHeight: 44, resize: 'vertical' }}
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                      />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" disabled={busyId === r.id} style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => reject(r)}>Confirm reject</button>
                        <button type="button" style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => { setFeedbackFor(null); setFeedback(''); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        disabled={busyId === r.id || loaBlocks}
                        title={loaBlocks ? 'Verify the LOA first' : undefined}
                        style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall, ...(loaBlocks ? { opacity: 0.5 } : {}) }}
                        onClick={() => approve(r)}
                      >Approve</button>
                      <button type="button" disabled={busyId === r.id} style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => { setFeedbackFor(r.id); setFeedback(''); }}>Reject</button>
                    </div>
                  )
                ) : (
                  <span style={{ fontSize: 11, color: C.textDim }}>view only</span>
                )}
              </td>
            </tr>
            {r.notProvidedBy382 && (
              <tr>
                <td colSpan={7} style={{ ...styles.td, paddingTop: 0 }}>
                  <TnChangeLoa req={r} canReview={canReview} onChanged={refetch} />
                </td>
              </tr>
            )}
            </Fragment>
            );
          })}
          {!loading && items.length === 0 && (
            <tr><td colSpan={7} style={{ ...styles.td, color: C.textMuted, textAlign: 'center' }}>No pending TN change requests</td></tr>
          )}
        </tbody>
      </table>
      {error && <div style={{ fontSize: 12, color: C.danger, marginTop: 12 }}>Failed to load TN change requests: {error}</div>}
    </div>
  );
}

/**
 * #685 — a stored billing address as ONE reviewable line, in whichever shape the
 * application carries.
 *
 * The queue detail is a read-only summary — every other row here is one line —
 * so the four parts are joined for display rather than given four rows. That
 * join is the same one `composeBillingAddress` does on the case
 * (apps/spa/src/pages/CaseDetail/tabs/OnboardingTab.tsx), which is where the
 * reviewer sees it next; showing the queue a differently-shaped version of the
 * value they are about to promote would be a needless second reading.
 *
 * A pre-split application has no parts, so the join is empty and the single
 * stored line is returned VERBATIM. Nothing here parses that line.
 */
export function billingAddressLine(
  contact?: { street?: string; city?: string; state?: string; zip?: string; address?: string },
): string {
  const str = (v?: string): string => (typeof v === 'string' ? v.trim() : '');
  const locality = [str(contact?.state), str(contact?.zip)].filter(x => x !== '').join(' ');
  const composed = [str(contact?.street), str(contact?.city), locality]
    .filter(x => x !== '').join(', ');
  return composed || str(contact?.address);
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.textDim, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color: value ? C.text : C.textDim }}>{value || '—'}</div>
    </div>
  );
}

// #432 — staff review queue for customer company-profile change requests.
//
// A customer admin asks; this is where somebody at 382 decides. It sits here,
// beside the TN-change queue, because it is the same kind of work by the same
// people: a customer has declared something and one of us has to act on it.
//
// WHAT ACCEPT ACTUALLY DOES, said on screen as well as here, and rewritten by
// #478. Which of two things it does depends on the FIELD, not on the request,
// and one request can carry both halves:
//
//   The trading name and the three contacts are WRITTEN into every in-scope
//   registration as part of accepting, along with the company's display name.
//   Nothing goes to CTIA for them — none is a field a CTIA payload carries.
//
//   The legal name and the business address write NOTHING. Accepting opens a
//   dual-controlled re-vet on each in-scope registration; the approval of that
//   re-vet, by a second person, is what writes the case and queues the sync.
//
// EVERY ROW LOADS ITS OWN AFFECTED-REGISTRATIONS PREVIEW, before the Accept
// button is offered and without being asked to. A company-level request fans
// out over every registration the company holds, and a preview the reviewer has
// to remember to open is one they will not open on the request that needed it.
// It is also where the two refusals become visible in advance: a legal name
// CTIA would reject, and a registration whose earlier re-vet is still open.
//
// Requested values stay selectable text: for a re-vet field the analyst reads
// the value off this card against a document.
// #478 — what accepting one change request will do to each of the company's
// registrations, rendered next to the button that will do it.
//
// EFFECT IS PER REGISTRATION and the words matter, because two of the six mean
// "nothing will happen" for completely different reasons and a reviewer who
// conflates them will chase the wrong thing:
//
//   WRITE / WRITE_AND_REVET / REVET — something happens. REVET is the one that
//     writes nothing yet, and says so.
//   NO_CHANGE  — this registration already holds the requested value.
//   EXCLUDED   — deliberately skipped: deleted, terminated, or under a final
//                determination. Rewriting a finished record does not bring it
//                up to date, it makes it untrue.
//   BLOCKED    — a re-vet from an earlier request is still open, and this is
//                what makes the whole acceptance refuse. It names that request
//                and when its re-vet opened, because "try later" without them
//                is not something anybody can act on.
//   LOGO_REVET / LOGO_WRITE — #768's call-logo change, listed only for the
//                one registration it names.
function AffectedRegistrationsPreview({ view }: { view: AffectedRegistrations | 'error' | undefined }) {
  if (view === undefined) {
    return <div style={{ fontSize: 12, color: C.textMuted }}>Working out what this touches…</div>;
  }
  if (view === 'error') {
    // Never silence this into a blank cell. The reviewer can still accept —
    // the server does its own checking — but they must know they are doing it
    // without the list.
    return (
      <div style={{ fontSize: 12, color: C.warn }}>
        Could not load the affected registrations. You can still accept; the server checks the same
        rules, and will refuse if a re-vet is already open or the name breaks a CTIA limit.
      </div>
    );
  }

  const tone = (effect: string): string => {
    if (effect === 'BLOCKED') return C.danger;
    if (effect === 'EXCLUDED' || effect === 'NO_CHANGE') return C.textMuted;
    return C.text;
  };
  const verb = (effect: string): string => {
    switch (effect) {
      case 'WRITE': return 'will be updated';
      case 'REVET': return 're-vet opens — nothing written yet';
      case 'WRITE_AND_REVET': return 'contact updated; re-vet opens for the rest';
      case 'NO_CHANGE': return 'already holds this';
      case 'EXCLUDED': return 'skipped';
      // #768
      case 'LOGO_REVET': return 'logo re-vet opens — CTIA keeps the current logo until it is approved';
      case 'LOGO_WRITE': return 'logo URL written — the case is still in vetting';
      default: return 'blocked';
    }
  };

  return (
    <div style={{ fontSize: 12 }}>
      {view.refusal && (
        <div style={{ color: C.danger, fontWeight: 600, marginBottom: 6 }} role="alert">
          Cannot accept: {view.refusal}
        </div>
      )}
      {view.registrations.length === 0 && (
        <div style={{ color: C.textMuted }}>
          This company holds no registrations, so accepting carries nothing across.
        </div>
      )}
      {view.registrations.map((reg) => (
        <div key={reg.caseId} style={{ marginBottom: 6, color: tone(reg.effect) }}>
          <div>
            <span style={{ userSelect: 'all' }}>{reg.caseId}</span>
            {' · '}{reg.status}{' · '}{verb(reg.effect)}
          </div>
          {reg.explanation && (
            <div style={{ fontSize: 11, color: reg.effect === 'BLOCKED' ? C.danger : C.textDim }}>
              {reg.explanation}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CompanyChangeRequestsQueue({ canReview }: { canReview: boolean }) {
  const { showToast, refreshStats } = useAppData();
  const [items, setItems] = useState<CompanyChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [feedbackFor, setFeedbackFor] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  // #478 — one preview per pending row, keyed by request id. 'error' rather
  // than a missing entry when the fetch fails: "not loaded yet" and "we could
  // not work out what this would touch" must not render identically on a card
  // whose whole job is telling the reviewer what they are about to do.
  const [affected, setAffected] = useState<Record<string, AffectedRegistrations | 'error'>>({});
  // #493 — a MAP, not a set: `has(id)` is still the "already asked" mark the
  // fetch effect filters on, and the value is the round that asked for it,
  // which is how a response works out whether it is still wanted.
  const requestedPreviews = useRef<Map<string, number>>(new Map());
  const previewRound = useRef(0);
  // #493 — the heartbeat those marks were last retired on, so a re-render
  // between ticks does not re-ask for previews it already has.
  const previewedAtTick = useRef(0);
  // #474 — third queue on this page, same heartbeat, paused mid-decision.
  const pollTick = usePollTick(busyId !== null);
  const seqRef = useRef(0);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  const runQuery = useCallback(async (background: boolean) => {
    const seq = ++seqRef.current;
    if (!background) setLoading(true);
    try {
      const page = await listCompanyChangeRequests({ status: 'PENDING' });
      if (seq !== seqRef.current) return;
      setItems(page.items);
      setError(null);
    } catch (err: unknown) {
      if (seq !== seqRef.current || background) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      // NOT `&& !background` — see the note in state/useCaseQuery.ts: that
      // guard let `loading` stick at true when a background tick superseded a
      // foreground run.
      if (seq === seqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { void runQuery(false); }, [runQuery, nonce]);
  // [pollTick] only, the same form as the other three background effects. This
  // queue's `runQuery` happens to be stable (useCallback with no deps), so
  // listing it would be harmless here and NOT harmless in the two paged
  // queries, where it changes with offset. One form everywhere means the rule
  // survives someone later giving this callback a dependency —
  // exhaustive-deps is off in this config, so this comment is the only
  // backstop.
  useEffect(() => {
    if (pollTick === 0) return;
    void runQuery(true);
  }, [pollTick]);

  // Load a preview for any row that does not have one, and reload every visible
  // row's preview on each heartbeat.
  //
  // #478 shipped this fetch-once and said why: a preview only changes when
  // somebody acts, and acting locally drops the entry so the next render
  // reloads it. #493 is the case that reasoning missed — the somebody acting is
  // often ANOTHER reviewer, whose acceptance opens a re-vet that BLOCKS this
  // request, and this screen went on offering an enabled Accept button over a
  // card that still said nothing was in the way. The server answers 409
  // regardless, so no control was at risk; a button that looks available and is
  // not is the page lying about what clicking will do, which on the screen
  // whose whole job is that is the bug.
  //
  // The refresh is keyed on POLLTICK, not on `items` changing. A background
  // refetch does hand back a fresh array every time in the real app, and
  // hanging the reload off that would work — right up until something memoises
  // the list and the previews silently stop refreshing with no test to notice.
  // The tick is the thing that actually means "a minute passed, re-ask".
  //
  // It also inherits the #474 pause for free: usePollTick drops ticks while a
  // decision is in flight, so a preview is never reloaded under the cursor of
  // the reviewer whose own Accept is mid-flight.
  //
  // "Already asked" is a REF, not the absence of a result. Keying the guard on
  // `affected[id] === undefined` looks equivalent and is not: a response that
  // is itself undefined leaves the entry indistinguishable from never having
  // asked, and the effect re-fires forever. That is not hypothetical — it hung
  // this page's own test run.
  useEffect(() => {
    // A heartbeat retires every mark at once, so the filter below asks for all
    // of them. Guarded on the tick VALUE rather than run unconditionally: this
    // effect also runs on every `items` change, so an unguarded clear would
    // fire a second full round the moment the tick's own list response landed,
    // re-asking for previews it had fetched milliseconds earlier.
    if (pollTick !== previewedAtTick.current) {
      previewedAtTick.current = pollTick;
      requestedPreviews.current.clear();
    }
    const missing = items.filter((r) => !requestedPreviews.current.has(r.id));
    if (missing.length === 0) return;
    // A token for this batch of requests and nothing more: what decides whether
    // a response is still current is the per-ROW check below, so the counter's
    // only job is never to repeat a value. Bumping it here rather than above
    // the early return merely avoids burning numbers on runs that ask for
    // nothing — correctness does not rest on where it sits.
    const round = ++previewRound.current;
    for (const r of missing) requestedPreviews.current.set(r.id, round);
    void Promise.all(missing.map(async (r) => {
      // Superseded is a per-ROW question, not a per-round one: a later round
      // that asked for a DIFFERENT row has not answered this one, and dropping
      // this answer would leave that row showing last minute's preview for
      // another whole minute — the staleness this change exists to retire.
      // Accepting a request drops its mark, so this also discards a preview
      // still in flight for a row the reviewer has just actioned.
      const stillWanted = () => requestedPreviews.current.get(r.id) === round;
      try {
        const view = await getAffectedRegistrations(r.id);
        if (stillWanted()) setAffected((prev) => ({ ...prev, [r.id]: view ?? 'error' }));
      } catch {
        if (stillWanted()) setAffected((prev) => ({ ...prev, [r.id]: 'error' }));
      }
    }));
    // NO cleanup, and that is the fix rather than an omission. The `live`
    // boolean this replaced was cleared by the cleanup React runs on every
    // dependency change — not just on unmount — so the list response landing
    // mid-round threw away every preview the round had in flight, and the
    // re-run found the marks already set and asked for nothing. The request
    // went out, its answer was discarded, and the card stayed stale. The one
    // thing the cleanup did that is now unhandled is a setState after unmount,
    // which React 18 ignores.
  }, [items, pollTick]);

  const apply = async (req: CompanyChangeRequest) => {
    setBusyId(req.id);
    try {
      const { propagation } = await applyCompanyChangeRequest(req.id);
      // Report what happened, not what was asked for. An acceptance that wrote
      // three of five registrations must not read as a clean apply, and one
      // that opened re-vets has not changed anything yet.
      const parts: string[] = [];
      if (propagation.written.length > 0) {
        parts.push(`${propagation.written.length} registration${propagation.written.length === 1 ? '' : 's'} updated`);
      }
      if (propagation.revetOpened.length > 0) {
        parts.push(`re-vet opened on ${propagation.revetOpened.length} — nothing is written until a second person approves`);
      }
      if (propagation.unchanged.length > 0) parts.push(`${propagation.unchanged.length} already matched`);
      if (propagation.excluded.length > 0) parts.push(`${propagation.excluded.length} skipped as not live`);
      // #768 — the call-logo change, on its one registration.
      if (propagation.logo) {
        parts.push(propagation.logo.effect === 'LOGO_REVET'
          ? `logo re-vet opened on ${propagation.logo.caseId} — CTIA keeps the current logo until a second person approves`
          : `logo URL written on ${propagation.logo.caseId}, which is still in vetting`);
      }
      if (parts.length === 0) parts.push('this company holds no live registrations, so nothing was carried across');
      showToast(`${req.id} accepted: ${parts.join('; ')}.`, 'success');
      requestedPreviews.current.delete(req.id);
      setAffected((prev) => { const next = { ...prev }; delete next[req.id]; return next; });
      refetch();
      refreshStats();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (req: CompanyChangeRequest) => {
    if (!feedback.trim()) {
      showToast('A reason is required — the customer sees it, and a refusal without one comes back as an email.');
      return;
    }
    setBusyId(req.id);
    try {
      await rejectCompanyChangeRequest(req.id, feedback.trim());
      showToast(`${req.id} declined.`, 'success');
      setFeedbackFor(null);
      setFeedback('');
      refetch();
      refreshStats();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ ...styles.card, marginTop: 24 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, marginTop: 0, marginBottom: 4 }}>
        Company profile change requests
      </h3>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>
        Customers cannot edit their own company details — these are what their registrations were
        vetted against. Accepting carries a trading name or a contact into every live registration
        straight away. A legal name or an address writes nothing: it opens a re-vet on each of them,
        and a second person&rsquo;s approval is what updates the case and re-asserts the identity to
        the Settlement Platform. Read what each request will touch before you accept it.
      </div>

      {loading && <div style={{ fontSize: 13, color: C.textMuted }}>Loading…</div>}
      {error && (
        <div style={{ fontSize: 13, color: C.danger }} role="alert">
          Could not load company change requests: {error}
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <div style={{ fontSize: 13, color: C.textMuted }}>Nothing waiting.</div>
      )}

      {items.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Request</th>
              <th style={styles.th}>Asked for</th>
              <th style={styles.th}>What accepting will touch</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td style={styles.td}>
                  <div style={{ color: C.text, fontWeight: 600 }}>{r.id}</div>
                  <div style={{ fontSize: 12, color: C.textMuted }}>{r.customerId}</div>
                  <div style={{ fontSize: 11, color: C.textDim }}>{fmtDateTime(r.submittedAt)}</div>
                  {/* #768 — a call-logo change names one registration. On a live
                      one it opens a logo re-vet; on one still in vetting it is
                      written, and the case's own review covers it. */}
                  {r.caseId && (
                    <div style={{ fontSize: 12, color: C.text, marginTop: 6 }}>
                      For registration <span style={{ userSelect: 'all' }}>{r.caseId}</span>
                    </div>
                  )}
                  {r.requiresRevet && (
                    <div style={{ ...styles.badge, color: C.warn, marginTop: 6 }}>
                      {r.caseId
                        ? 'Logo re-check — two signatures on a live registration'
                        : 'Opens a re-vet — needs two signatures'}
                    </div>
                  )}
                  {r.note && (
                    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6, fontStyle: 'italic' }}>
                      &ldquo;{r.note}&rdquo;
                    </div>
                  )}
                </td>

                <td style={styles.td}>
                  {/* Selectable text, one field per line. The reviewer retypes
                      every one of these except the trading name. */}
                  {/* Canonical order, not Object.keys: the insertion order of
                      a jsonb round-trip is not a promise, and the reviewer and
                      the customer looking at the same request should read the
                      fields in the same sequence. Any key outside the known
                      vocabulary still renders, after the known ones, rather
                      than being silently dropped. */}
                  {[
                    ...COMPANY_FIELD_ORDER.filter((f) => r.requested[f] !== undefined),
                    ...Object.keys(r.requested).filter(
                      (f) => !(COMPANY_FIELD_ORDER as readonly string[]).includes(f),
                    ),
                  ].map((field) => (
                    <div key={field} style={{ fontSize: 13, marginBottom: 4 }}>
                      <div style={{ fontSize: 10, color: C.textDim, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {COMPANY_FIELD_LABEL[field] ?? field}
                      </div>
                      <div style={{ color: C.text, userSelect: 'all' }}>
                        {formatCompanyFieldValue(field, r.requested[field])}
                      </div>
                    </div>
                  ))}
                </td>

                <td style={styles.td}>
                  <AffectedRegistrationsPreview view={affected[r.id]} />
                </td>

                <td style={styles.td}>
                  {!canReview && (
                    <div style={{ fontSize: 12, color: C.textMuted }}>
                      Your role can view but not decide these.
                    </div>
                  )}
                  {canReview && feedbackFor === r.id && (
                    <div style={{ marginBottom: 8 }}>
                      <textarea
                        aria-label={`Reason for declining ${r.id}`}
                        style={{ ...styles.textarea, minHeight: 60 }}
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="What does the customer need to do? They see this."
                      />
                    </div>
                  )}
                  {canReview && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {feedbackFor === r.id ? (
                        <>
                          <button
                            type="button"
                            disabled={busyId === r.id}
                            style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }}
                            onClick={() => void reject(r)}
                          >Confirm decline</button>
                          <button
                            type="button"
                            style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                            onClick={() => { setFeedbackFor(null); setFeedback(''); }}
                          >Cancel</button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            // Disabled only on a preview that says the server
                            // WILL refuse — never on a preview that failed to
                            // load. A card that cannot reach the preview
                            // endpoint must not become a card that cannot
                            // decide anything; the server refuses for itself.
                            disabled={busyId === r.id || (affected[r.id] !== undefined
                              && affected[r.id] !== 'error'
                              && !(affected[r.id] as AffectedRegistrations).acceptable)}
                            style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }}
                            onClick={() => void apply(r)}
                          >Accept</button>
                          <button
                            type="button"
                            disabled={busyId === r.id}
                            style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                            onClick={() => { setFeedbackFor(r.id); setFeedback(''); }}
                          >Decline</button>
                        </>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
